import { useEffect, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  ShieldOff,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Footer } from "@/components/invoice-layout";
import {
  approveBill,
  fetchBill,
  rejectBill,
  type BillApprovalError,
  type PublicBillResponse,
} from "@/api/public";
import {
  DOLFIN_ACCENT,
  parseBrandColour,
  pickForeground,
  tintFor,
} from "@/lib/brand";
import { formatCurrency, formatDate } from "@/lib/format";

const DEFAULT_TITLE = "Approve bill";

type Stage =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "not-authorised" }
  | { kind: "network-error" }
  | { kind: "bill"; bill: PublicBillResponse }
  | { kind: "submitting"; bill: PublicBillResponse; action: "approve" | "reject" };

export function BillApprovalPage() {
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const bill = "bill" in stage ? stage.bill : null;
  const brand = parseBrandColour(bill?.clientPrimaryColour) ?? DOLFIN_ACCENT;

  useEffect(() => {
    if (!bill) {
      document.title = DEFAULT_TITLE;
      return;
    }
    const supplier = bill.supplierName ?? "bill";
    document.title = `${supplier} — ${formatCurrency(bill.totalAmount, bill.currency)}`;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [bill?.supplierName, bill?.totalAmount, bill?.currency]);

  useEffect(() => {
    if (!token) {
      setStage({ kind: "invalid" });
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await fetchBill(token);
      if (cancelled) return;
      if (!result.ok) {
        setStage(stageForError(result.error));
        return;
      }
      setStage({ kind: "bill", bill: result.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const handleApprove = async () => {
    if (!token || !bill) return;
    setStage({ kind: "submitting", bill, action: "approve" });
    const result = await approveBill(token);
    if (!result.ok) {
      setStage(stageForError(result.error));
      return;
    }
    setStage({ kind: "bill", bill: result.data });
  };

  const handleReject = async (reason: string) => {
    if (!token || !bill) return;
    setShowRejectModal(false);
    setStage({ kind: "submitting", bill, action: "reject" });
    const result = await rejectBill(token, reason);
    if (!result.ok) {
      setStage(stageForError(result.error));
      return;
    }
    setStage({ kind: "bill", bill: result.data });
  };

  const pageStyle = {
    backgroundColor: tintFor(brand),
    ["--ring" as string]: brand,
  } as CSSProperties;

  return (
    <div className="flex min-h-screen items-center justify-center p-4 sm:p-6" style={pageStyle}>
      <div className="w-full max-w-lg">
        <StageView
          stage={stage}
          brand={brand}
          pdfHref={`/b/${token ?? ""}/pdf`}
          onApprove={handleApprove}
          onOpenReject={() => setShowRejectModal(true)}
          onRetry={() => {
            setStage({ kind: "loading" });
            setReloadKey((k) => k + 1);
          }}
        />
        <Footer />
        {showRejectModal && bill ? (
          <RejectModal
            brand={brand}
            onCancel={() => setShowRejectModal(false)}
            onSubmit={handleReject}
          />
        ) : null}
      </div>
    </div>
  );
}

function stageForError(error: BillApprovalError): Stage {
  switch (error.kind) {
    case "invalid-token":
      return { kind: "invalid" };
    case "not-authorised":
      return { kind: "not-authorised" };
    case "network":
      return { kind: "network-error" };
    default:
      return { kind: "invalid" };
  }
}

function StageView({
  stage,
  brand,
  pdfHref,
  onApprove,
  onOpenReject,
  onRetry,
}: {
  stage: Stage;
  brand: string;
  pdfHref: string;
  onApprove: () => void;
  onOpenReject: () => void;
  onRetry: () => void;
}) {
  switch (stage.kind) {
    case "loading":
      return (
        <Card>
          <CardContent className="flex min-h-[260px] items-center justify-center">
            <Spinner />
          </CardContent>
        </Card>
      );
    case "invalid":
      return (
        <Card>
          <CardHeader className="items-center gap-3 text-center">
            <AlertTriangle className="h-10 w-10 text-muted-foreground" />
            <CardTitle>This approval link is no longer valid</CardTitle>
            <CardDescription>Ask whoever sent it to resend a fresh link.</CardDescription>
          </CardHeader>
          <CardContent />
        </Card>
      );
    case "not-authorised":
      return (
        <Card>
          <CardHeader className="items-center gap-3 text-center">
            <ShieldOff className="h-10 w-10 text-muted-foreground" />
            <CardTitle>You can no longer review this bill</CardTitle>
            <CardDescription>
              Your access to this organisation's bill review has been removed since this email was sent.
            </CardDescription>
          </CardHeader>
          <CardContent />
        </Card>
      );
    case "network-error":
      return (
        <Card>
          <CardHeader className="items-center gap-3 text-center">
            <AlertTriangle className="h-10 w-10 text-muted-foreground" />
            <CardTitle>Couldn't load this bill</CardTitle>
            <CardDescription>Check your connection and try again.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={onRetry}>Retry</Button>
          </CardContent>
        </Card>
      );
    case "bill":
    case "submitting": {
      const submitting = stage.kind === "submitting";
      return (
        <BillCard
          bill={stage.bill}
          brand={brand}
          submitting={submitting}
          submittingAction={submitting ? stage.action : null}
          onApprove={onApprove}
          onOpenReject={onOpenReject}
          pdfHref={pdfHref}
        />
      );
    }
  }
}

function BillCard({
  bill,
  brand,
  submitting,
  submittingAction,
  pdfHref,
  onApprove,
  onOpenReject,
}: {
  bill: PublicBillResponse;
  brand: string;
  submitting: boolean;
  submittingAction: "approve" | "reject" | null;
  pdfHref: string;
  onApprove: () => void;
  onOpenReject: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        {bill.clientLogoUrl ? (
          <img
            src={bill.clientLogoUrl}
            alt=""
            className="mb-3 h-8 w-auto"
            style={{ maxWidth: 160 }}
          />
        ) : null}
        <CardTitle>{bill.supplierName ?? "Bill"}</CardTitle>
        <CardDescription>
          {formatCurrency(bill.totalAmount, bill.currency)}
          {bill.dueDate ? ` · due ${formatDate(bill.dueDate)}` : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <BillFacts bill={bill} />
        {bill.pdfSignedUrl ? (
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
            style={{ color: brand }}
          >
            View original PDF <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
        <StateBanner bill={bill} brand={brand} />
        {bill.state === "NeedsApproval" ? (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={onApprove}
              disabled={submitting}
              className="flex-1"
              style={{ backgroundColor: brand, color: pickForeground(brand) }}
            >
              {submitting && submittingAction === "approve" ? <Spinner className="h-4 w-4" /> : "Approve"}
            </Button>
            <Button
              onClick={onOpenReject}
              disabled={submitting}
              variant="outline"
              className="flex-1"
            >
              {submitting && submittingAction === "reject" ? <Spinner className="h-4 w-4" /> : "Reject"}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BillFacts({ bill }: { bill: PublicBillResponse }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {bill.invoiceNumber ? <Fact label="Invoice number" value={bill.invoiceNumber} /> : null}
      {bill.invoiceDate ? <Fact label="Issued" value={formatDate(bill.invoiceDate) ?? ""} /> : null}
      {bill.dueDate ? <Fact label="Due" value={formatDate(bill.dueDate) ?? ""} /> : null}
      <Fact label="Subtotal" value={formatCurrency(bill.subTotal, bill.currency)} />
      {bill.taxAmount > 0 ? (
        <Fact label="Tax" value={formatCurrency(bill.taxAmount, bill.currency)} />
      ) : null}
      <Fact label="Total" value={formatCurrency(bill.totalAmount, bill.currency)} />
      {bill.paymentTerms ? <Fact label="Payment terms" value={bill.paymentTerms} /> : null}
    </dl>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </>
  );
}

function StateBanner({ bill, brand }: { bill: PublicBillResponse; brand: string }) {
  switch (bill.state) {
    case "NeedsApproval":
      return null;
    case "Approved":
      return (
        <Banner
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />}
          title="Approved · awaiting payment"
          detail={bill.approvedByEmail ? `Approved by ${bill.approvedByEmail}` : null}
        />
      );
    case "Rejected":
      return (
        <Banner
          icon={<XCircle className="h-5 w-5 text-red-500" />}
          title="Rejected"
          detail={
            <>
              {bill.rejectionReason ? <p>{bill.rejectionReason}</p> : null}
              {bill.rejectedByEmail ? (
                <p className="text-xs text-muted-foreground">Rejected by {bill.rejectedByEmail}</p>
              ) : null}
              <p className="pt-1 text-xs text-muted-foreground">
                A new bill needs to be sent. Contact the sender.
              </p>
            </>
          }
        />
      );
    case "Scheduled":
      return (
        <Banner
          icon={<CheckCircle2 className="h-5 w-5" style={{ color: brand }} />}
          title="Payment scheduled"
          detail={bill.paymentDate ? `For ${formatDate(bill.paymentDate)}` : null}
        />
      );
    case "Paid":
      return (
        <Banner
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />}
          title="Paid"
          detail={bill.paidAt ? `On ${formatDate(bill.paidAt)}` : null}
        />
      );
    default:
      return (
        <Banner
          icon={<AlertTriangle className="h-5 w-5 text-muted-foreground" />}
          title="This bill isn't ready for review"
          detail="Check back later."
        />
      );
  }
}

function Banner({
  icon,
  title,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  detail: React.ReactNode;
}) {
  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <div className="flex items-start gap-2">
        {icon}
        <div className="space-y-1 text-sm">
          <p className="font-medium">{title}</p>
          {detail ? <div className="text-muted-foreground">{detail}</div> : null}
        </div>
      </div>
    </div>
  );
}

function RejectModal({
  brand,
  onCancel,
  onSubmit,
}: {
  brand: string;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const valid = trimmed.length >= 3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reject this bill</CardTitle>
          <CardDescription>
            Tell the sender why so they can void it or send a corrected one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1024}
            rows={4}
            className="w-full rounded-md border bg-background p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="e.g. Amount is wrong — should be £450 not £540"
          />
          <p className="text-xs text-muted-foreground">{trimmed.length} / 1024</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              disabled={!valid}
              onClick={() => onSubmit(trimmed)}
              style={{ backgroundColor: brand, color: pickForeground(brand) }}
            >
              Reject
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
